import React, { useState } from "react";
import PropTypes from "prop-types";
import Chart from "ikizmet-react-apexcharts";
import HeatMapModal from "./views/heatMapModal";
import { xlsxExport } from "../../utils/xlsxExport";
import {
  addDelimiterToDisplayValue,
  calculateTableHeight,
  formatTooltip,
  getExcelRowValue,
  getFormat
} from "./heatMapUtils";
import useChartConfig from "./hooks/useChartConfig";

const isEmptyObject = (obj) => Object.keys(obj).length === 0 && obj.constructor === Object;

const HeatMap = ({
  handleDataClick,
  groupByTimeOfDay,
  modalProps,
  groupByWeekDay,
  modalTableData,
  tableDataLoading,
  isUserQa,
  showAddons: { exportPng, exportXlsx } = {},
  ...rest
}) => {
  const chartEvents = {
    dataPointSelection: (_, __, config) => handleModalOpen(config),
    dataPointMouseEnter: (event, _, config) => handleMouseEnter(event, config),
    dataPointMouseLeave: () => handleMouseLeave()
  };

  const { chartConfig } = useChartConfig({ exportPng, ...rest }, chartEvents);
  const [modalDataState, setModalDataState] = useState(null);
  const [tooltip, setTooltip] = useState({
    left: 0,
    data: "",
    top: 0,
    zIndex: 0,
    opacity: 0
  });

  const handleModalOpen = ({ seriesIndex, dataPointIndex }) => {
    let { modalData, valueSymbol, toolTipData, displayValue } =
      chartConfig.series[seriesIndex].data[dataPointIndex];

    if (isEmptyObject(modalData) || modalData?.showWithoutModalWindow) {
      return null;
    }
    const weekday = groupByTimeOfDay ? "" : toolTipData.split(":")[0];

    handleDataClick(modalData.tableDataUid);

    setModalDataState({
      ...modalData,
      title: {
        name: `${weekday} ${chartConfig.series[seriesIndex].name}`,
        value: modalData?.value || displayValue,
        valueSymbol: valueSymbol
      },
      value: modalData.utilization ?? modalData.value,
      valueChange: modalData.utilizationChange ?? modalData.valueChange,
      valuePrevPeriod: modalData.utilizationPrevPeriod ?? modalData.valuePrevPeriod,
      ...modalProps
    });
  };

  const handleMouseEnter = (event, config) => {
    const modalData = chartConfig.series[config.seriesIndex].data[config.dataPointIndex].modalData;
    const positionX = event.srcElement.attributes.x.value;
    const positionY = event.srcElement.attributes.y.value;
    const offsetX = config.w.globals.translateX;
    const spacingOffsetTop = -3;

    if (!isEmptyObject(modalData) && !modalData?.showWithoutModalWindow) {
      setTooltip({
        data: chartConfig.series[config.seriesIndex].data[config.dataPointIndex].toolTipData,
        left: parseInt(positionX) + offsetX,
        top: parseInt(positionY) + spacingOffsetTop,
        opacity: 1,
        zIndex: 1
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({
      ...tooltip,
      data: "",
      opacity: 0,
      zIndex: 0
    });
  };

  const excelColumnsFormat = (valueSymbol) => {
    const format = getFormat(valueSymbol);

    let columns = Array(chartConfig.options.xaxis.categories.length).fill(format);
    //skip formatting for the first column as it is time column
    columns.unshift({});

    return columns;
  };

  const exportExcel = () => {
    //groupByWeekDay does not contain rows
    let series = groupByWeekDay
      ? chartConfig.series
      : chartConfig.series.toReversed().filter((row) => !!row.name);

    let formattedData = series.map((row) => [
      row.name,
      ...row.data.map((data) => getExcelRowValue(data.modalData.tableDataUid, data.displayValue))
    ]);

    xlsxExport({
      headers: ["", ...chartConfig.options.xaxis.categories],
      data: formattedData,
      fileName: "HeatMap",
      columns: excelColumnsFormat(series[0].data[0].valueSymbol)
    });
  };
  return (
    <div data-testid="heatMap">
      {exportPng && (
        <div className="chart-addons-control-item">
          <a
            title="Export to PNG"
            onClick={() => {
              chartConfig.downloadPng();
            }}
          >
            <span className="glyphicon glyphicon-picture" />
          </a>
        </div>
      )}

      {exportXlsx && (
        <div className="chart-addons-control-item">
          <a
            title="Export to Excel"
            onClick={() => {
              exportExcel();
            }}
          >
            <span className="glyphicon glyphicon-download-alt" />
          </a>
        </div>
      )}
      {modalDataState && (
        <HeatMapModal
          isUserQa={isUserQa}
          modalData={modalDataState}
          tableData={modalTableData}
          tableDataLoading={tableDataLoading}
          handleModalClose={() => setModalDataState(null)}
          groupByTimeOfDay={groupByTimeOfDay}
        />
      )}

      <div id="class-utilization-chart">
        {tooltip.data !== "" && (
          <div
            id="metric-tooltip"
            style={{
              ...styles.toolTip,
              left: tooltip.left,
              top: tooltip.top,
              opacity: tooltip.opacity,
              zIndex: tooltip.zIndex
            }}
          >
            <p style={{ margin: 0 }}>{formatTooltip(tooltip.data, groupByTimeOfDay)}</p>
          </div>
        )}

        <Chart
          options={chartConfig.options}
          series={addDelimiterToDisplayValue(chartConfig.series)}
          type="heatmap"
          height={calculateTableHeight(chartConfig.series.length)}
          width={groupByTimeOfDay ? "330px" : "1350"}
        />
      </div>
    </div>
  );
};

let styles = {
  toolTip: {
    display: "flex",
    alignItems: "center",
    height: "35px",
    padding: "0 30px",
    position: "absolute",
    background: "#202229",
    opacity: 1,
    transition: "opacity 0.5s",
    color: "white",
    borderRadius: "10px",
    border: "solid 1px #353942"
  }
};

HeatMap.propTypes = {
  stroke: PropTypes.number,
  heatMapRadius: PropTypes.number,
  displayAsBubbles: PropTypes.bool,
  series: PropTypes.array,
  filledBubbles: PropTypes.bool,
  modalTableData: PropTypes.object,
  tableDataLoading: PropTypes.bool,
  handleDataClick: PropTypes.func,
  selectedCategory: PropTypes.string,
  showPrevPeriod: PropTypes.bool,
  tableHeight: PropTypes.number,
  modalProps: PropTypes.object,
  groupByTimeOfDay: PropTypes.bool
};

export default HeatMap;
